import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createIntegrationLink, listIntegrationLinks } from '../../api/business/endpoints';
import type { IntegrationLink, IntegrationLinkIssue, IntegrationLinkStatus } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { CompanySelector } from '../../components/CompanySelector';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatNumber } from '../../components/format';
import { IntegrationStatusChip } from '../../components/IntegrationStatusChip';
import { PageHeader } from '../../components/PageHeader';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { sortRows, useSort } from '../../components/useSort';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { useLanguage } from '../../i18n/LanguageContext';

import { ConnectionTokenDialog } from './ConnectionTokenDialog';
import { ConnectorDownloads } from './ConnectorDownloads';
import { LinkSettingsDialog } from './LinkSettingsDialog';

type LinkSortKey = 'name' | 'status' | 'lastSeenAt' | 'mapped' | 'unmatched' | 'openConflicts';

const VALUE_OF: Record<LinkSortKey, (link: IntegrationLink) => string | number | null> = {
  name: (link) => link.name,
  status: (link) => link.status,
  lastSeenAt: (link) => link.lastSeenAt,
  mapped: (link) => link.mapped,
  unmatched: (link) => link.unmatched,
  openConflicts: (link) => link.openConflicts,
};

/** The e-shops linked to the selected company (R0009): the whole list, so it sorts in the browser. */
export const IntegrationsPage = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const selection = useSelectedCompany('integrations.read');
  const company = selection.company;
  const canManage = company !== null && permissions.can('integrations.manage', company.id);
  const [status, setStatus] = useState<IntegrationLinkStatus | ''>('');
  const [creating, setCreating] = useState(false);
  const [issue, setIssue] = useState<IntegrationLinkIssue | null>(null);
  const sort = useSort<LinkSortKey>('name');

  const query = useQuery({
    queryKey: ['integration-links', company?.id],
    enabled: company !== null,
    queryFn: () => listIntegrationLinks(company?.id ?? ''),
  });
  const create = useAppMutation(createIntegrationLink, ['integration-links'], (result) => {
    setCreating(false);
    setIssue(result);
  });

  const rows = sortRows(
    (query.data ?? []).filter((link) => status === '' || link.status === status),
    sort.direction,
    VALUE_OF[sort.key],
  );

  return (
    <>
      <PageHeader
        title={t('integrations.title')}
        subtitle={t('integrations.subtitle')}
        action={
          <Stack direction="row" spacing={2} alignItems="center">
            <CompanySelector companies={selection.companies} value={company} onChange={selection.select} />
            {canManage ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
                {t('integrations.create')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice error={selection.error ?? query.error} onRetry={() => void query.refetch()} />
      {!selection.isLoading && company === null ? (
        <Paper variant="outlined">
          <EmptyState title={t('integrations.noCompany')} detail={t('integrations.noCompanyDetail')} />
        </Paper>
      ) : (
        <Paper variant="outlined">
          <TableFilters>
            <TextField
              select
              size="small"
              label={t('field.status')}
              value={status}
              onChange={(e) => setStatus(e.target.value as IntegrationLinkStatus | '')}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {(['PENDING', 'ACTIVE', 'DISABLED', 'REVOKED'] as const).map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`linkStatus.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </TableFilters>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableCell sort={sort} column="name">
                    {t('integrations.shop')}
                  </SortableCell>
                  <SortableCell sort={sort} column="status">
                    {t('field.status')}
                  </SortableCell>
                  <SortableCell sort={sort} column="lastSeenAt">
                    {t('integrations.lastSeen')}
                  </SortableCell>
                  <SortableCell sort={sort} column="mapped" align="right">
                    {t('integrations.mapped')}
                  </SortableCell>
                  <SortableCell sort={sort} column="unmatched" align="right">
                    {t('integrations.unmatched')}
                  </SortableCell>
                  <SortableCell sort={sort} column="openConflicts" align="right">
                    {t('integrations.openConflicts')}
                  </SortableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.isLoading ? (
                  <TableSkeletonRows columns={6} />
                ) : (
                  rows.map((link) => (
                    <TableRow
                      key={link.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/integrations/${link.companyId}/${link.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 560 }}>
                          {link.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {link.shopUrl ?? t('integrations.notConnected')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IntegrationStatusChip status={link.status} />
                        {link.status === 'ACTIVE' && link.firstSyncState !== 'DONE' ? (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {t('integrations.awaitingChoice')}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(link.lastSeenAt, language)}
                      </TableCell>
                      <TableCell align="right">{formatNumber(link.mapped, language)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: link.unmatched > 0 ? 'warning.main' : undefined }}
                      >
                        {formatNumber(link.unmatched, language)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: link.openConflicts > 0 ? 'error.main' : undefined }}
                      >
                        {formatNumber(link.openConflicts, language)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!query.isLoading && rows.length === 0 ? (
            <EmptyState title={t('integrations.empty')} detail={t('integrations.emptyDetail')} />
          ) : null}
        </Paper>
      )}
      {company !== null ? <ConnectorDownloads companyId={company.id} /> : null}
      {creating && company !== null ? (
        <LinkSettingsDialog
          link={null}
          onClose={() => setCreating(false)}
          isBusy={create.isPending}
          error={create.error}
          onSubmit={(settings, platform) => create.mutate({ ...settings, companyId: company.id, platform })}
        />
      ) : null}
      {issue !== null ? <ConnectionTokenDialog issue={issue} onClose={() => setIssue(null)} /> : null}
    </>
  );
};
